import { useState } from 'react';
import { Btn } from '../../components/ui/Btn.jsx';
import { Inp } from '../../components/ui/Inputs.jsx';
import { C } from '../../theme/palette';
import { FONCTIONS, modifieContacts } from './personnes';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Petite pastille de la fonction, comme sur la maquette.
 *
 * @param {object} props propriétés
 * @param {string} [props.fonction] fonction affichée
 * @returns {import('react').ReactElement|null} la pastille
 */
export function PastilleFonction({ fonction }) {
  if (!fonction) return null;
  return (
    <span
      style={{
        fontSize: 8.5,
        fontWeight: 800,
        color: C.navyM,
        background: C.navyXL,
        borderRadius: 99,
        padding: '2px 8px',
        marginLeft: 6,
        whiteSpace: 'nowrap',
      }}
    >
      {fonction}
    </span>
  );
}

/**
 * Le petit formulaire « Nouvelle personne » : nom, e-mail, fonction.
 *
 * @param {object} props propriétés
 * @param {Function} props.onAjoute reçoit {nom, email, fonction}
 * @param {Function} [props.onAnnule] ferme le formulaire
 * @returns {import('react').ReactElement} le formulaire
 */
export function NouvellePersonne({ onAjoute, onAnnule }) {
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [fonction, setFonction] = useState('Technicien');
  const [erreur, setErreur] = useState('');
  const ajoute = () => {
    if (!nom.trim()) return setErreur('Le nom est obligatoire.');
    if (!EMAIL.test(email.trim())) return setErreur('L’adresse e-mail est invalide.');
    onAjoute({ nom: nom.trim(), email: email.trim(), fonction });
    setNom('');
    setEmail('');
    setErreur('');
  };
  return (
    <div
      style={{
        border: '2px solid ' + C.teal,
        background: C.tealL,
        borderRadius: 12,
        padding: 12,
        marginTop: 8,
      }}
    >
      <div style={{ fontSize: 10.5, fontWeight: 800, color: C.teal, marginBottom: 8 }}>
        ＋ Nouvelle personne
      </div>
      <Inp
        label="Prénom et nom"
        value={nom}
        onChange={(e) => setNom(e.target.value)}
        placeholder="Prénom et nom"
      />
      <Inp
        label="E-mail"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="E-mail"
      />
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {FONCTIONS.map((f) => {
          const on = f === fonction;
          return (
            <button
              key={f}
              type="button"
              aria-pressed={on}
              onClick={() => setFonction(f)}
              style={{
                fontSize: 9.5,
                fontWeight: 700,
                padding: '6px 12px',
                borderRadius: 99,
                cursor: 'pointer',
                background: on ? C.teal : C.white,
                color: on ? '#fff' : C.grey,
                border: '1px solid ' + (on ? C.teal : C.border),
              }}
            >
              {f}
            </button>
          );
        })}
      </div>
      {erreur && (
        <div role="alert" style={{ fontSize: 9.5, color: C.red, marginBottom: 8 }}>
          {erreur}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        {onAnnule && <Btn label="Annuler" ghost color={C.grey} onClick={onAnnule} />}
        <div style={{ flex: 1 }}>
          <Btn label="✓ Ajouter" color={C.green} full onClick={ajoute} />
        </div>
      </div>
    </div>
  );
}

/**
 * Bouton pointillé « ＋ Ajouter une personne ».
 *
 * @param {object} props propriétés
 * @param {string} props.label libellé
 * @param {Function} props.onClick ouvre le formulaire
 * @returns {import('react').ReactElement} le bouton
 */
export function BoutonAjoutPersonne({ label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%',
        border: '1.5px dashed ' + C.teal,
        background: C.white,
        color: C.teal,
        borderRadius: 12,
        padding: '11px 12px',
        fontSize: 11,
        fontWeight: 800,
        cursor: 'pointer',
        marginTop: 8,
      }}
    >
      {label}
    </button>
  );
}

/**
 * « 👥 Personnes à contacter » de la fiche d'une société cliente : le
 * directeur, les techniciens, les collaborateurs, la comptabilité. Ce sont
 * eux qui reçoivent le catalogue, chacun avec son propre lien.
 *
 * @param {object} props propriétés
 * @param {object} props.client client affiché
 * @param {Function} props.setClients mise à jour de la collection des clients
 * @returns {import('react').ReactElement} la liste et sa saisie
 */
export function PersonnesAContacter({ client, setClients }) {
  const [ouvert, setOuvert] = useState(false);
  const contacts = client.contacts || [];
  return (
    <div style={{ padding: 13 }}>
      {contacts.length === 0 && !ouvert && (
        <div style={{ fontSize: 9.5, color: C.muted, fontStyle: 'italic', marginBottom: 4 }}>
          Ajoute le directeur, les techniciens et les collaborateurs de la société, avec leur
          e-mail.
        </div>
      )}
      {contacts.length > 0 && (
        <ul aria-label="Personnes à contacter" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {contacts.map((p, rang) => (
            <li
              key={rang + p.email}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 0',
                borderBottom: rang < contacts.length - 1 ? '1px solid ' + C.border : 'none',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: C.ink }}>
                  {p.nom}
                  <PastilleFonction fonction={p.fonction} />
                </div>
                <div style={{ fontSize: 9.5, color: C.muted, overflowWrap: 'anywhere' }}>
                  {p.email || '—'}
                </div>
              </div>
              <button
                type="button"
                aria-label={'Retirer ' + p.nom}
                onClick={() =>
                  modifieContacts(setClients, client.id, (l) => l.filter((_, k) => k !== rang))
                }
                style={{
                  border: 'none',
                  background: 'none',
                  color: C.red,
                  fontSize: 13,
                  cursor: 'pointer',
                  width: 30,
                  height: 30,
                }}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
      {ouvert ? (
        <NouvellePersonne
          onAjoute={(p) => {
            modifieContacts(setClients, client.id, (l) => [...l, p]);
            setOuvert(false);
          }}
          onAnnule={() => setOuvert(false)}
        />
      ) : (
        <BoutonAjoutPersonne label="＋ Nouvelle personne" onClick={() => setOuvert(true)} />
      )}
    </div>
  );
}
